import { type UploadCertificateFormData } from "@dune/types";
import {
	Button,
	ControlledInput,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@dune/ui";
import React from "react";
import { FormProvider } from "react-hook-form";
import { toast } from "sonner";
import { useUploadContractorCertificateMutation } from "../../../../../api/mutations/contractors.mutations";
import { S3FileInput } from "../../../../../component/common/S3FileInput";
import { useUploadCertificate } from "./useUploadCertificate";

interface UploadCertificateModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	contractorId: number;
}

const UploadCertificateModal: React.FC<UploadCertificateModalProps> = ({
	open,
	onOpenChange,
	contractorId,
}) => {
	const formHook = useUploadCertificate();
	const { control, handleSubmit, formState, resetForm, setSelectedFile } =
		formHook;
	const { errors, isValid } = formState;

	const uploadCertificateMutation = useUploadContractorCertificateMutation();

	const onSubmit = async (data: UploadCertificateFormData) => {
		if (!data.selectedFile) {
			toast.error("Veuillez sélectionner un fichier");
			return;
		}

		try {
			await uploadCertificateMutation.mutateAsync({
				contractorId,
				name: data.selectedFile.name,
				key: data.selectedFile.key,
				bucket: data.selectedFile.bucket,
				mimeType: data.selectedFile.type,
				validityStartDate: data.validityStartDate,
				validityEndDate: data.validityEndDate,
				activities: data.activities?.trim()
					? data.activities
							.split(",")
							.map((a) => a.trim())
							.filter(Boolean)
					: undefined,
				insurerSiren: data.insurerSiren || undefined,
			});

			onOpenChange(false);
			resetForm();
		} catch (error) {
			console.error("Upload failed:", error);
		}
	};

	const handleModalClose = (newOpen: boolean) => {
		if (!newOpen) {
			resetForm();
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleModalClose}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>Ajouter une attestation</DialogTitle>
					<DialogDescription>
						Ajoutez une nouvelle attestation décennale pour cet intervenant
					</DialogDescription>
				</DialogHeader>

				<FormProvider {...formHook}>
					<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<ControlledInput
								label="Début de validité *"
								control={control}
								name="validityStartDate"
								type="date"
								error={errors.validityStartDate?.message}
							/>
							<ControlledInput
								label="Fin de validité *"
								control={control}
								name="validityEndDate"
								type="date"
								error={errors.validityEndDate?.message}
							/>
						</div>

						<ControlledInput
							label="SIREN de l'assureur"
							control={control}
							name="insurerSiren"
							placeholder="123456789"
							maxLength={9}
							error={errors.insurerSiren?.message}
						/>

						<ControlledInput
							label="Activités couvertes"
							control={control}
							name="activities"
							placeholder="Séparez les activités par des virgules"
							error={errors.activities?.message}
						/>

						<div>
							<div className="text-sm font-medium mb-2">Fichier *</div>
							<S3FileInput
								key={`upload-modal-${open}`}
								bucket="private"
								onUploadSuccess={setSelectedFile}
								hidePreviewButton={true}
								subText="Formats acceptés : PDF, DOCX, PNG, JPG (max. 10MB)"
							/>
							{errors.selectedFile && (
								<p className="text-sm text-red-500 mt-1">
									{errors.selectedFile.message}
								</p>
							)}
						</div>

						<DialogFooter>
							<Button variant="outline" onClick={() => onOpenChange(false)}>
								Annuler
							</Button>
							<Button
								type="submit"
								disabled={!isValid || uploadCertificateMutation.isPending}
								isLoading={uploadCertificateMutation.isPending}
							>
								Ajouter l'attestation
							</Button>
						</DialogFooter>
					</form>
				</FormProvider>
			</DialogContent>
		</Dialog>
	);
};

export default UploadCertificateModal;
